import React, { useState } from 'react';
import EditRoleModal from './EditRoleModal';
import { Pencil, Search, Trash2, UserPlus, X } from 'lucide-react';

const RolesManagementModal = ({
  isOpen,
  onClose,
  onAddRole,
  onEditRole,
  onDeleteRole,
  availablePages,
  standardRoles = [],
  embedded = false
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    roleName: '',
    displayName: '',
    description: '',
    permissions: []
  });

  const handlePermissionChange = (permissionId) => {
    const updatedPermissions = formData.permissions.includes(permissionId)
      ? formData.permissions.filter(p => p !== permissionId)
      : [...formData.permissions, permissionId];

    setFormData({
      ...formData,
      permissions: updatedPermissions
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onAddRole(formData);
      setFormData({
        roleName: '',
        displayName: '',
        description: '',
        permissions: []
      });
      setShowAddForm(false);
    } catch (error) {
      setError(error.message || 'Ошибка при создании роли');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      roleName: '',
      displayName: '',
      description: '',
      permissions: []
    });
    setShowAddForm(false);
    setError('');
    onClose();
  };

  const handleEditClick = (role) => {
    setEditingRole(role);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (roleId) => {
    setError('');
    if (!roleId) {
      setError('Невозможно удалить роль: ID роли не найден. Попробуйте обновить страницу.');
      return;
    }
    try {
      await onDeleteRole(roleId);
    } catch (error) {
      setError(error.message || 'Ошибка при удалении роли');
    }
  };

  const filteredRoles = searchTerm
    ? standardRoles.filter(role =>
        role.roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : standardRoles;

  if (!isOpen) return null;

  return (
    <>
      <div className={embedded ? 'w-full' : 'fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto'}>
        <div className={embedded
          ? 'bg-white w-full flex flex-col'
          : 'bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col my-auto'}>
            {/* Header */}
            {!embedded && <div className="bg-gradient-to-r from-green-600 to-green-800 px-6 md:px-8 py-5 flex justify-between items-center">
              <h2 className={`${embedded ? 'text-xl text-gray-900' : 'text-3xl text-white'} font-bold flex items-center`}>
                <span className={`${embedded ? 'mr-2 text-2xl' : 'mr-3 text-4xl'}`}>🎭</span>
                Управление ролями
              </h2>
              <button
                onClick={handleClose}
                className="text-white hover:text-gray-200 text-3xl font-bold transition-colors"
              >
                ×
              </button>
            </div>}

            {/* Content */}
            <div className={`flex-1 ${embedded ? 'overflow-visible p-4' : 'overflow-y-auto p-8'}`}>
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              {/* Controls */}
              <div className="mb-5 flex flex-wrap items-center gap-2.5">
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 ${showAddForm
                    ? 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus:ring-gray-100'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 focus:ring-blue-100'}`}
                >
                  {showAddForm ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                  {showAddForm ? 'Отменить' : 'Добавить роль'}
                </button>

                {/* Search */}
                <div className="relative min-w-[240px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Поиск по названию роли"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              {/* Add Role Form */}
              {showAddForm && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Создать новую роль</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column - Basic Info */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Идентификатор роли * <span className="text-xs text-gray-500">(латиница, цифры, _, -)</span>
                          </label>
                          <input
                            type="text"
                            placeholder="manager_b24"
                            value={formData.roleName}
                            onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                            pattern="[a-zA-Z0-9_\-]+"
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Отображаемое название *
                          </label>
                          <input
                            type="text"
                            placeholder="Менеджер B24"
                            value={formData.displayName}
                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Описание роли
                          </label>
                          <textarea
                            placeholder="Краткое описание роли"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                      </div>

                      {/* Right Column - Permissions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Разрешения ({formData.permissions.length})
                        </label>
                        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
                          {Object.entries(availablePages).map(([category, pages]) => (
                            <div key={category} className="mb-4">
                              <h5 className="font-semibold text-gray-600 mb-2">{category}</h5>
                              <div className="grid grid-cols-1 gap-2">
                                {pages.map(page => (
                                  <label key={page.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions.includes(page.id)}
                                      onChange={() => handlePermissionChange(page.id)}
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm">{page.icon} {page.name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Отмена
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
                      >
                        {loading ? 'Создание...' : 'Создать роль'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Roles Grid */}
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                  <h3 className="text-sm font-medium text-gray-600">
                    Ролей: <span className="font-semibold text-gray-900">{filteredRoles.length}</span>
                    {filteredRoles.length !== standardRoles.length && <span className="text-gray-400"> из {standardRoles.length}</span>}
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredRoles.map(role => (
                    <div key={role.id || role.roleName} className="rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-blue-200 hover:bg-gray-50/30">
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h4 className="mb-1 truncate text-base font-semibold text-gray-900" title={role.displayName}>{role.displayName}</h4>
                          <p className="inline-block rounded bg-gray-100/80 px-2 py-0.5 font-mono text-xs text-gray-500">
                            {role.roleName}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={() => handleEditClick(role)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            title="Редактировать"
                            aria-label={`Редактировать роль ${role.displayName}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(role.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                            title="Удалить"
                            aria-label={`Удалить роль ${role.displayName}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2 text-xs">
                          <span className="text-gray-500">Разрешения</span>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">{role.permissions.length}</span>
                        </div>

                        {role.permissions.length > 0 && (
                          <div className="pt-3">
                            <div className="flex flex-wrap gap-1.5">
                              {role.permissions.slice(0, 3).map(perm => {
                                const page = Object.values(availablePages).flat().find(p => p.id === perm);
                                return (
                                  <span key={perm} className="rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs text-gray-600">
                                    {page?.icon} {page?.name || perm}
                                  </span>
                                );
                              })}
                              {role.permissions.length > 3 && (
                                <span className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-500">
                                  +{role.permissions.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {filteredRoles.length === 0 && (
                  <div className="flex flex-col items-center p-12 text-center text-gray-400">
                    <Search className="mb-2 h-5 w-5" />
                    <p className="text-sm">Роли не найдены</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            {!embedded && <div className="bg-gray-50 px-8 py-4 flex justify-end border-t">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Закрыть
              </button>
            </div>}
          </div>
      </div>

      {/* Edit Role Modal */}
      {showEditModal && editingRole && (
        <EditRoleModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingRole(null);
          }}
          onEditRole={onEditRole}
          role={editingRole}
          availablePages={availablePages}
        />
      )}
    </>
  );
};

export default RolesManagementModal;
